/* ==========================================================================
   OLIVER KRAUS — theme behaviour
   One small module system for every OK section:
     <div data-ok-module="film"> … </div>
   Modules mount on page load and on Shopify theme-editor section load, and
   are destroyed on section unload, so duplicated / reordered sections work
   independently and leave no observers, listeners or timers behind.
   Content never depends on JavaScript to be readable: reveal-hidden states
   exist only while html.ok-js is set (see snippets/ok-brand-head.liquid).
   ========================================================================== */
(function () {
  'use strict';

  var OK = window.OK = window.OK || {};
  var defs = {};
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqNarrow = window.matchMedia('(max-width: 900px)');
  var mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  OK.reduce = function () { return mqReduce.matches; };
  OK.narrow = function () { return mqNarrow.matches; };
  OK.canHover = function () { return mqHover.matches; };

  /* ---------- helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  OK.$ = $; OK.$$ = $$;

  /* Listener bag: every module collects its listeners so destroy() is exact. */
  function Bag() { this.fns = []; }
  Bag.prototype.on = function (target, type, fn, opts) {
    if (!target) return;
    if (target.addEventListener) target.addEventListener(type, fn, opts);
    else if (target.addListener && type === 'change') target.addListener(fn); // old Safari MQL
    var self = this;
    this.fns.push(function () {
      if (target.removeEventListener) target.removeEventListener(type, fn, opts);
      else if (target.removeListener) target.removeListener(fn);
    });
    return self;
  };
  Bag.prototype.add = function (fn) { this.fns.push(fn); };
  Bag.prototype.clear = function () { while (this.fns.length) { try { this.fns.pop()(); } catch (e) {} } };
  OK.Bag = Bag;

  function formatMoney(cents, fmt) {
    fmt = fmt || (window.OK_MONEY_FORMAT) || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var n = cents / 100;
    function group(num, dec, th, dp) {
      var parts = num.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return parts.join(dp);
    }
    switch (match && match[1]) {
      case 'amount_no_decimals': value = group(n, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = group(n, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = group(n, 0, '.', ','); break;
      case 'amount_with_space_separator': value = group(n, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = group(n, 2, ' ', '.'); break;
      default: value = group(n, 2, ',', '.');
    }
    return match ? fmt.replace(match[0], value) : value;
  }
  OK.money = formatMoney;

  /* ---------- module registry ---------- */
  OK.define = function (name, factory) {
    defs[name] = factory;
    if (OK.ready) OK.mount(document);
  };

  OK.mount = function (root) {
    root = root || document;
    var els = $$('[data-ok-module]', root);
    if (root.nodeType === 1 && root.hasAttribute && root.hasAttribute('data-ok-module')) els.unshift(root);
    els.forEach(function (el) {
      el.__ok = el.__ok || {};
      el.getAttribute('data-ok-module').split(/\s+/).forEach(function (name) {
        if (!name || el.__ok[name] || !defs[name]) return;
        try { el.__ok[name] = defs[name](el) || {}; }
        catch (err) { if (window.console) console.error('[OK] ' + name, err); }
      });
    });
    Reveal.scan(root);
  };

  OK.unmount = function (root) {
    var els = $$('[data-ok-module]', root);
    if (root.hasAttribute && root.hasAttribute('data-ok-module')) els.unshift(root);
    els.forEach(function (el) {
      if (!el.__ok) return;
      Object.keys(el.__ok).forEach(function (name) {
        var inst = el.__ok[name];
        if (inst && inst.destroy) { try { inst.destroy(); } catch (e) {} }
      });
      el.__ok = {};
    });
    Reveal.forget(root);
  };

  function call(root, method, arg) {
    var els = $$('[data-ok-module]', root);
    if (root.hasAttribute && root.hasAttribute('data-ok-module')) els.unshift(root);
    els.forEach(function (el) {
      if (!el.__ok) return;
      Object.keys(el.__ok).forEach(function (n) {
        var inst = el.__ok[n];
        if (inst && typeof inst[method] === 'function') { try { inst[method](arg); } catch (e) {} }
      });
    });
  }

  /* ---------- scroll reveals ---------- */
  var Reveal = (function () {
    var io = null, pending = [], ticking = false, sweepOn = false;
    function show(el) { el.classList.add('is-in'); }
    function sweep() {
      ticking = false;
      var vh = window.innerHeight;
      pending = pending.filter(function (el) {
        if (!el.isConnected) return false;
        var r = el.getBoundingClientRect();
        var visible = r.top < vh * 0.96 && r.bottom > 0;
        if (visible) show(el);
        return !visible;
      });
      if (!pending.length && sweepOn) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        sweepOn = false;
      }
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(sweep); } }
    function scan(root) {
      var els = $$('[data-ok-reveal]:not(.is-in)', root);
      if (!els.length) return;
      if (OK.reduce() || !('IntersectionObserver' in window)) { els.forEach(show); return; }
      if (!io) {
        io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { show(en.target); io.unobserve(en.target); }
          });
        }, { threshold: 0.04, rootMargin: '0px 0px -6% 0px' });
      }
      els.forEach(function (el) {
        if (el.closest('[data-ok-noreveal]')) { show(el); return; }
        io.observe(el);
        pending.push(el);
      });
      if (!sweepOn) {
        sweepOn = true;
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
      }
      setTimeout(sweep, 60);
      setTimeout(sweep, 1200);
    }
    function revealAll(root) { $$('[data-ok-reveal]', root).forEach(show); }
    function forget(root) {
      pending = pending.filter(function (el) {
        if (root.contains(el)) { if (io) io.unobserve(el); return false; }
        return true;
      });
    }
    function onReduce() { if (OK.reduce()) { pending.forEach(show); pending = []; } }
    if (mqReduce.addEventListener) mqReduce.addEventListener('change', onReduce);
    else if (mqReduce.addListener) mqReduce.addListener(onReduce);
    return { scan: scan, revealAll: revealAll, forget: forget };
  })();
  OK.reveal = Reveal;

  /* ---------- scroll lock (menus, dialogs) ---------- */
  var lockDepth = 0, savedY = 0;
  OK.lock = function (on) {
    var b = document.body, h = document.documentElement;
    if (on) {
      if (lockDepth++ > 0) return;
      savedY = window.scrollY;
      b.classList.add('ok-body-locked');
      b.style.top = -savedY + 'px';
      h.style.overflow = 'hidden';
    } else {
      if (lockDepth === 0 || --lockDepth > 0) return;
      b.classList.remove('ok-body-locked');
      b.style.top = '';
      h.style.overflow = '';
      window.scrollTo(0, savedY);
    }
  };

  /* ---------- cart (Ajax API + the theme's own drawer) ---------- */
  OK.cart = {
    host: function () { return document.querySelector('cart-drawer') || document.querySelector('cart-notification'); },
    sectionIds: function () {
      var host = this.host();
      if (!host || typeof host.getSectionsToRender !== 'function') return [];
      try {
        return host.getSectionsToRender().filter(function (s) {
          return s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
        }).map(function (s) { return s.id; });
      } catch (e) { return []; }
    },
    add: function (items) {
      var ids = this.sectionIds();
      var body = { items: items };
      if (ids.length) { body.sections = ids; body.sections_url = window.location.pathname; }
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            var err = new Error(data.description || data.message || 'Could not add to cart.');
            err.data = data; throw err;
          }
          return data;
        });
      });
    },
    reveal: function (response, trigger) {
      var host = this.host();
      if (host && host.tagName === 'CART-DRAWER' && typeof host.renderContents === 'function' && response && response.sections) {
        try {
          if (trigger && typeof host.setActiveElement === 'function') host.setActiveElement(trigger);
          host.renderContents(response);
          var inner = host.querySelector('.drawer__inner');
          if (inner && !host.querySelector('.drawer__inner-empty')) host.classList.remove('is-empty');
          this.notify();
          return;
        } catch (e) { /* fall through */ }
      }
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = root + 'cart';
    },
    notify: function () {
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'ok-theme' }); } catch (e) {}
      }
    }
  };

  /* ======================================================================
     MODULES
     ====================================================================== */

  /* ---------- header + mobile menu ---------- */
  OK.define('header', function (el) {
    var bag = new Bag();
    var burger = $('[data-ok-burger]', el);
    var menu = $('[data-ok-menu]', el);
    var open = false;
    function onScroll() { el.classList.toggle('is-scrolled', window.scrollY > 40); }
    function set(v) {
      if (v === open || !burger) return;
      open = v;
      el.classList.toggle('is-menu-open', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      burger.setAttribute('aria-label', open ? 'Close menu' : 'Menu');
      OK.lock(open);
      if (open) { var first = menu && $('a', menu); if (first) first.focus(); }
      else if (burger) burger.focus({ preventScroll: true });
    }
    bag.on(window, 'scroll', onScroll, { passive: true });
    onScroll();
    if (burger) bag.on(burger, 'click', function () { set(!open); });
    if (menu) bag.on(menu, 'click', function (e) { if (e.target.closest('a')) set(false); });
    bag.on(document, 'keydown', function (e) {
      if (!open) return;
      if (e.key === 'Escape') { e.preventDefault(); set(false); return; }
      if (e.key === 'Tab') {
        var f = [burger].concat($$('a', menu));
        var i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    });
    bag.on(mqNarrow, 'change', function () { if (!mqNarrow.matches) set(false); });
    return { destroy: function () { if (open) { open = false; OK.lock(false); } bag.clear(); } };
  });

  /* ---------- footer newsletter panel ---------- */
  OK.define('footer', function (el) {
    var bag = new Bag();
    var toggle = $('[data-ok-news-toggle]', el);
    var panel = $('[data-ok-news]', el);
    if (!toggle || !panel) return {};
    var input = $('input[type="email"]', panel);
    function set(v, focus) {
      panel.hidden = !v;
      toggle.setAttribute('aria-expanded', v ? 'true' : 'false');
      if (v && focus && input) input.focus();
      if (!v && focus) toggle.focus();
    }
    bag.on(toggle, 'click', function () { set(panel.hidden, true); });
    var close = $('[data-ok-news-close]', panel);
    if (close) bag.on(close, 'click', function () { set(false, true); });
    bag.on(panel, 'keydown', function (e) { if (e.key === 'Escape') set(false, true); });
    if (panel.hasAttribute('data-ok-news-open')) {
      set(true, false);
      setTimeout(function () { panel.scrollIntoView({ block: 'center', behavior: OK.reduce() ? 'auto' : 'smooth' }); }, 60);
    }
    return { destroy: function () { bag.clear(); } };
  });

  /* ---------- campaign film ---------- */
  OK.define('film', function (el) {
    var bag = new Bag();
    var v = $('video', el);
    if (!v) return {};
    var btn = $('[data-ok-film-btn]', el);
    var still = $('[data-ok-film-still]', el);
    var status = $('[data-ok-film-status]', el);
    var label = el.getAttribute('data-label') || 'campaign film';
    var srcD = el.getAttribute('data-src') || '';
    var srcM = el.getAttribute('data-src-mobile') || '';
    var autoplay = el.getAttribute('data-autoplay') === 'true';
    var st = { userPaused: false, userPlayed: false, visible: false, failed: false, loaded: '', near: false };
    var io = null, ioNear = null;

    function wanted() { return (mqNarrow.matches && srcM) ? srcM : srcD; }
    function attach() {
      var want = wanted();
      if (!want || st.loaded === want) return;
      var wasPlaying = !v.paused;
      st.loaded = want;
      v.setAttribute('preload', 'metadata');
      v.src = want;
      if (wasPlaying) play();
    }
    function allowed() {
      if (st.failed || st.userPaused || !st.visible || document.hidden) return false;
      if (st.userPlayed) return true;
      return autoplay && !OK.reduce();
    }
    function play() {
      attach();
      var p;
      try { p = v.play(); } catch (e) { fail(); return; }
      if (p && p.catch) p.catch(function (err) {
        var n = err && err.name;
        if (n === 'NotAllowedError') { st.userPlayed = false; sync(); return; } // autoplay blocked: offer Play
        if (n === 'AbortError') { sync(); return; }                          // superseded by pause/src change
        fail();
      });
    }
    function apply() {
      if (allowed()) { if (v.paused) play(); }
      else if (!v.paused) v.pause();
      sync();
    }
    function say(msg) { if (status) status.textContent = msg; }
    function fail() {
      if (st.failed) return;
      st.failed = true;
      el.classList.add('is-failed');
      if (still) still.hidden = false;
      try { v.pause(); } catch (e) {}
      say('The ' + label + ' could not be played. A still image is shown.');
      sync();
    }
    function retry() {
      st.failed = false;
      st.userPaused = false;
      st.userPlayed = true;
      st.loaded = '';
      el.classList.remove('is-failed');
      if (still) still.hidden = true;
      v.removeAttribute('src');
      try { v.load(); } catch (e) {}
      say('Retrying the ' + label + '.');
      play();
      sync();
    }
    function sync() {
      var playing = !v.paused && !v.ended;
      var state = st.failed ? 'failed' : (playing ? 'playing' : 'paused');
      el.setAttribute('data-state', state);
      if (!btn) return;
      btn.hidden = false;
      btn.setAttribute('aria-label', (state === 'failed' ? 'Retry ' : state === 'playing' ? 'Pause ' : 'Play ') + label);
    }

    if (btn) bag.on(btn, 'click', function () {
      if (st.failed) { retry(); return; }
      if (!v.paused) { st.userPaused = true; st.userPlayed = false; v.pause(); say('Paused.'); }
      else { st.userPaused = false; st.userPlayed = true; st.visible = true; play(); say('Playing.'); }
      sync();
    });
    ['play', 'playing', 'pause', 'ended', 'emptied'].forEach(function (t) { bag.on(v, t, sync); });
    bag.on(v, 'error', function () { if (v.error && st.loaded) fail(); });
    bag.on(document, 'visibilitychange', apply);
    bag.on(mqReduce, 'change', function () {
      if (OK.reduce()) st.userPlayed = false; // a fresh, explicit Play is required
      apply();
    });
    bag.on(mqNarrow, 'change', function () {
      if (st.loaded && st.loaded !== wanted()) { var was = !v.paused; st.loaded = ''; if (was) play(); else attach(); }
    });

    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { st.visible = e.isIntersecting; });
        apply();
      }, { threshold: 0.25 });
      io.observe(el);
      // Attach the source shortly before the panel is seen (not under reduced motion).
      ioNear = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (e.isIntersecting && autoplay && !OK.reduce() && !st.userPaused) attach();
        });
      }, { rootMargin: '300px 0px' });
      ioNear.observe(el);
    } else {
      st.visible = true;
      apply();
    }
    sync();
    return {
      destroy: function () {
        if (io) io.disconnect();
        if (ioNear) ioNear.disconnect();
        try { v.pause(); } catch (e) {}
        bag.clear();
      },
      select: function () { st.visible = true; apply(); }
    };
  });

  /* ---------- marquee strip (pause on hover, focus, button, offscreen) ---------- */
  OK.define('marquee', function (el) {
    var bag = new Bag();
    var btn = $('[data-ok-marquee-btn]', el);
    var user = false, hover = false, offscreen = false, io = null;
    function apply() {
      var paused = user || hover || offscreen;
      el.classList.toggle('is-paused', paused);
      if (btn) {
        btn.setAttribute('aria-pressed', user ? 'true' : 'false');
        btn.setAttribute('aria-label', user ? 'Play moving text' : 'Pause moving text');
      }
    }
    bag.on(el, 'mouseenter', function () { hover = true; apply(); });
    bag.on(el, 'mouseleave', function () { hover = false; apply(); });
    bag.on(el, 'focusin', function () { hover = true; apply(); });
    bag.on(el, 'focusout', function () { hover = false; apply(); });
    if (btn) bag.on(btn, 'click', function () { user = !user; apply(); });
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (es) { es.forEach(function (e) { offscreen = !e.isIntersecting; }); apply(); });
      io.observe(el);
    }
    apply();
    return { destroy: function () { if (io) io.disconnect(); bag.clear(); } };
  });

  /* ---------- homepage hemp panel: one-shot entrance ---------- */
  OK.define('seen', function (el) {
    var io = null, timer = null;
    function done() { el.classList.add('is-seen'); if (io) io.disconnect(); clearTimeout(timer); }
    if (OK.reduce() || !('IntersectionObserver' in window) || designMode) { done(); return {}; }
    io = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) done(); }); }, { threshold: 0.2 });
    io.observe(el);
    // safety: if it is already in view but the observer never fires
    timer = setTimeout(function () {
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 1.1 && r.bottom > 0) done();
    }, 1200);
    return { destroy: function () { if (io) io.disconnect(); clearTimeout(timer); }, select: done };
  });

  /* ---------- horizontal rail with buttons and optional drift ---------- */
  OK.define('rail', function (el) {
    var bag = new Bag();
    var track = $('[data-ok-rail-track]', el);
    if (!track) return {};
    var prev = $('[data-ok-rail-prev]', el), next = $('[data-ok-rail-next]', el);
    var drift = el.getAttribute('data-drift') === 'true';
    var paused = false, stopped = false, visible = false, raf = 0, x = 0, io = null;
    function max() { return Math.max(0, track.scrollWidth - track.clientWidth); }
    function nudge(dir) {
      stopped = true;
      track.style.scrollSnapType = '';
      var target = Math.max(0, Math.min(max(), track.scrollLeft + dir * Math.max(240, track.clientWidth * 0.6)));
      track.style.scrollBehavior = OK.reduce() ? 'auto' : 'smooth';
      track.scrollLeft = target;
      if (Math.abs(track.scrollLeft - target) > 8) { track.style.scrollBehavior = 'auto'; track.scrollLeft = target; }
      x = target;
      updateButtons();
    }
    function updateButtons() {
      if (prev) prev.disabled = track.scrollLeft <= 2 && !drift;
      if (next) next.disabled = track.scrollLeft >= max() - 2 && !drift;
    }
    function tick() {
      raf = 0;
      if (!visible || stopped || paused || OK.reduce() || OK.narrow() || !OK.canHover()) { track.style.scrollSnapType = ''; return; }
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none'; /* mandatory snapping would pull sub-pixel drift back to the card edge */
      if (Math.abs(track.scrollLeft - x) > 2) x = track.scrollLeft;
      x = x >= max() - 1 ? 0 : x + 0.35;
      track.scrollLeft = x;
      raf = requestAnimationFrame(tick);
    }
    function kick() { if (drift && !raf) raf = requestAnimationFrame(tick); }
    if (prev) bag.on(prev, 'click', function () { nudge(-1); });
    if (next) bag.on(next, 'click', function () { nudge(1); });
    ['mouseenter', 'focusin', 'pointerdown', 'touchstart'].forEach(function (t) { bag.on(track, t, function () { paused = true; }, { passive: true }); });
    ['mouseleave', 'focusout'].forEach(function (t) { bag.on(track, t, function () { paused = false; kick(); }); });
    bag.on(track, 'scroll', updateButtons, { passive: true });
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (es) { es.forEach(function (e) { visible = e.isIntersecting; }); if (visible) kick(); }, { threshold: 0.15 });
      io.observe(track);
    }
    updateButtons();
    return {
      destroy: function () { if (io) io.disconnect(); cancelAnimationFrame(raf); bag.clear(); },
      blockSelect: function (block) {
        stopped = true;
        if (block && track.contains(block)) track.scrollLeft = block.offsetLeft - track.offsetLeft - 20;
      }
    };
  });

  /* ---------- builders: rotation (N pieces) and set (top + bottom) ---------- */
  function readJSON(el) {
    var node = $('script[type="application/json"][data-ok-data]', el);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  OK.define('builder', function (el) {
    var bag = new Bag();
    var data = readJSON(el);
    if (!data || !data.products || !data.products.length) return {};
    var fmt = data.moneyFormat;
    var mode = data.mode; // 'rotation' | 'set'
    var products = data.products;
    var byId = {};
    products.forEach(function (p) { byId[p.key] = p; });
    var cta = $('[data-ok-builder-cta]', el);
    var totalEl = $('[data-ok-builder-total]', el);
    var noteEl = $('[data-ok-builder-note]', el);
    var msg = $('[data-ok-builder-msg]', el);
    var busy = false;

    // state per row: { key, size }
    var rows = $$('[data-ok-row]', el).map(function (row) {
      var sel = $('[data-ok-choice][aria-pressed="true"]', row);
      return { el: row, key: sel ? sel.getAttribute('data-ok-choice') : null, size: null };
    });
    var count = mode === 'rotation' ? Number(el.getAttribute('data-count') || 2) : rows.length;

    function productFor(row) { return byId[row.key] || null; }
    function variantFor(row) {
      var p = productFor(row);
      if (!p || !row.size) return null;
      for (var i = 0; i < p.variants.length; i++) if (p.variants[i].option === row.size) return p.variants[i];
      return null;
    }
    function esc(t) { return String(t).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
    function renderSizes(row) {
      var p = productFor(row);
      var wrap = $('[data-ok-sizes]', row.el);
      if (!wrap || !p) return;
      var html = '';
      p.options.forEach(function (opt) {
        var v = null;
        for (var i = 0; i < p.variants.length; i++) if (p.variants[i].option === opt) { v = p.variants[i]; break; }
        var avail = !!(v && v.available);
        var on = row.size === opt;
        html += '<button type="button" class="ok-size' + (avail ? '' : ' is-out') + '" data-ok-size="' + esc(opt) + '" aria-pressed="' + (on ? 'true' : 'false') + '"' +
          (avail ? '' : ' disabled aria-label="' + esc(opt + ' — ' + (data.strings.soldOut || 'Sold out')) + '"') + '>' + esc(opt) + (avail ? '' : '<i aria-hidden="true"></i>') + '</button>';
      });
      wrap.innerHTML = html;
      wrap.style.setProperty('--ok-size-cols', Math.min(p.options.length, 5));
      if (row.size) {
        var cur = variantFor(row);
        if (!cur || !cur.available) row.size = null;
      }
    }
    function update() {
      var total = 0, complete = true, parts = [];
      rows.forEach(function (row, i) {
        var active = mode !== 'rotation' || i < count;
        row.el.hidden = !active;
        if (!active) return;
        var p = productFor(row);
        var v = variantFor(row);
        var price = v ? v.price : (p ? p.price : 0);
        total += price;
        if (!v || !v.available) complete = false;
        var lbl = $('[data-ok-row-selected]', row.el);
        var txt = p ? (p.short || p.title) + (row.size ? ' · ' + (data.strings.sizeWord || 'Size') + ' ' + row.size : '') : '';
        if (lbl) lbl.textContent = txt;
        parts.push(p ? (p.short || p.title) + (row.size ? ' ' + row.size : '') : '');
      });
      if (totalEl) totalEl.textContent = formatMoney(total, fmt);
      if (cta) {
        var base = mode === 'rotation' ? (data.strings.addRotation || 'Add rotation') : (data.strings.addSet || 'Add set');
        cta.textContent = busy ? (data.strings.adding || 'Adding') : base + ' — ' + formatMoney(total, fmt);
        cta.setAttribute('aria-disabled', busy ? 'true' : 'false');
      }
      if (noteEl) {
        noteEl.textContent = (mode === 'rotation' && count === 3 && data.strings.threeNote) ? data.strings.threeNote : parts.filter(Boolean).join(' · ');
      }
      $$('[data-ok-count]', el).forEach(function (b) { b.setAttribute('aria-pressed', Number(b.getAttribute('data-ok-count')) === count ? 'true' : 'false'); });
      return complete;
    }

    rows.forEach(function (row) {
      renderSizes(row);
      bag.on(row.el, 'click', function (e) {
        var c = e.target.closest('[data-ok-choice]');
        if (c && row.el.contains(c)) {
          row.key = c.getAttribute('data-ok-choice');
          $$('[data-ok-choice]', row.el).forEach(function (b) { b.setAttribute('aria-pressed', b === c ? 'true' : 'false'); });
          renderSizes(row);
          if (msg) msg.textContent = '';
          update();
          return;
        }
        var s = e.target.closest('[data-ok-size]');
        if (s && !s.disabled) {
          row.size = s.getAttribute('data-ok-size');
          $$('[data-ok-size]', row.el).forEach(function (b) { b.setAttribute('aria-pressed', b === s ? 'true' : 'false'); });
          row.el.classList.remove('is-error');
          if (msg) msg.textContent = '';
          update();
        }
      });
    });
    $$('[data-ok-count]', el).forEach(function (b) {
      bag.on(b, 'click', function () { count = Number(b.getAttribute('data-ok-count')); if (msg) msg.textContent = ''; update(); });
    });
    if (cta) bag.on(cta, 'click', function () {
      if (busy) return;
      var missing = null;
      rows.forEach(function (row, i) {
        if (mode === 'rotation' && i >= count) return;
        var v = variantFor(row);
        if (!v || !v.available) { row.el.classList.add('is-error'); if (!missing) missing = row; }
      });
      if (missing) {
        if (msg) msg.textContent = data.strings.chooseSizes || 'Choose a size for each piece.';
        var first = $('[data-ok-size]:not([disabled])', missing.el);
        if (first) first.focus();
        return;
      }
      var qty = {};
      rows.forEach(function (row, i) {
        if (mode === 'rotation' && i >= count) return;
        var v = variantFor(row);
        qty[v.id] = (qty[v.id] || 0) + 1;
      });
      var items = Object.keys(qty).map(function (id) { return { id: Number(id), quantity: qty[id] }; });
      busy = true; update();
      OK.cart.add(items).then(function (res) {
        busy = false; update();
        if (msg) msg.textContent = data.strings.added || 'Added to your bag.';
        OK.cart.reveal(res, cta);
      }).catch(function (err) {
        busy = false; update();
        if (msg) msg.textContent = (err && err.message) || data.strings.error || 'Something went wrong. Please try again.';
      });
    });
    update();
    return { destroy: function () { bag.clear(); } };
  });

  /* ======================================================================
     BOOT + THEME EDITOR LIFECYCLE
     ====================================================================== */
  function boot() {
    OK.ready = true;
    OK.mount(document);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) { OK.mount(e.target); if (designMode) Reveal.revealAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { OK.unmount(e.target); });
  document.addEventListener('shopify:section:select', function (e) { Reveal.revealAll(e.target); call(e.target, 'select'); });
  document.addEventListener('shopify:section:deselect', function (e) { call(e.target, 'deselect'); });
  document.addEventListener('shopify:block:select', function (e) {
    var section = e.target.closest('.shopify-section') || document;
    Reveal.revealAll(section);
    call(section, 'blockSelect', e.target);
  });
})();

/* ---------- Preview routes (unpublished theme only) ----------
   Until launch the Men/Women collections and About page still use their
   live templates, and /pages/hemp does not exist. While this theme is
   being previewed (Shopify.theme.role is not "main"), internal links to
   those addresses open the new OLIVER KRAUS templates via ?view=.
   Once the theme is published and templates are assigned this does nothing. */
(function () {
  var role = window.Shopify && Shopify.theme && Shopify.theme.role;
  if (!role || role === 'main') return;
  var MAP = {
    '/collections/men': '/collections/men?view=ok-men',
    '/collections/women': '/collections/women?view=ok-women',
    '/pages/about-us': '/pages/about-us?view=ok-about',
    '/pages/hemp': '/pages/about-us?view=ok-hemp'
  };
  function route(a) {
    var raw = a.getAttribute('href');
    if (!raw || raw.charAt(0) === '#') return;
    var u;
    try { u = new URL(raw, location.href); } catch (e) { return; }
    if (u.origin !== location.origin || u.searchParams.has('view')) return;
    var target = MAP[u.pathname.replace(/\/+$/, '')];
    if (!target) return;
    var t = new URL(target, location.origin);
    u.searchParams.forEach(function (v, k) { t.searchParams.set(k, v); });
    a.setAttribute('href', t.pathname + t.search + u.hash);
  }
  function all() { Array.prototype.forEach.call(document.querySelectorAll('a[href]'), route); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all); else all();
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (a) route(a);
  }, true);
  document.addEventListener('shopify:section:load', all);
})();

/* ---------------------------------------------------------------------------
   The Vee accessibility widget renders its "Accessibility Policy" link with an
   empty href unless the URL is set in the Vee dashboard, which leaves a dead
   link inside the panel. Point it at the store's own statement once the widget
   has mounted. Does nothing if Vee is not on the page or already has a URL.
   --------------------------------------------------------------------------- */
(function () {
  var URL_ACC = '/pages/accessibility';
  var tries = 0;
  var timer = setInterval(function () {
    var a = document.getElementById('_vp_acc_policy');
    if (a) {
      if (!a.getAttribute('href')) a.setAttribute('href', URL_ACC);
      clearInterval(timer);
      return;
    }
    if (++tries > 40) clearInterval(timer);
  }, 500);
})();

/* ---------------------------------------------------------------------------
   Cart drawer focus. The base theme moves focus into the drawer on the
   drawer's transitionend, which does not fire reliably here, and its focus
   trap only guards the first and last focusable it found at open time. The
   result was that a keyboard or screen-reader user never entered the drawer
   and could Tab straight out of it into the page behind. This moves focus in
   when the drawer opens and keeps Tab inside it until the drawer closes.
   --------------------------------------------------------------------------- */
(function () {
  var drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof MutationObserver === 'undefined') return;

  var SEL = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function isOpen() { return drawer.classList.contains('active'); }

  function focusables() {
    var inner = drawer.querySelector('.drawer__inner');
    if (!inner) return [];
    return Array.prototype.filter.call(inner.querySelectorAll(SEL), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length;
    });
  }

  function focusInside() {
    if (!isOpen() || drawer.contains(document.activeElement)) return;
    var list = focusables();
    var target = drawer.querySelector('.drawer__close') || list[0];
    if (target && target.focus) target.focus();
  }

  document.addEventListener('keydown', function (e) {
    if (!isOpen()) return;
    var key = e.key || e.code;
    if (key !== 'Tab' && key !== 'Escape') return;
    if (key === 'Escape') {
      /* The accessibility widget binds its own Escape handler and swallows the
         event before the base theme sees it, so close the drawer here. */
      e.preventDefault();
      var opener = document.getElementById('cart-icon-bubble');
      if (typeof drawer.close === 'function') drawer.close();
      else drawer.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      /* The widget's own handler runs after this one and pulls focus into its
         panel, so put focus back on the opener once that has settled. */
      if (opener && opener.focus) {
        setTimeout(function () { opener.focus(); }, 0);
        setTimeout(function () { if (!drawer.contains(document.activeElement)) opener.focus(); }, 180);
      }
      return;
    }
    var list = focusables();
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (!drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, true);

  /* The accessibility launcher is a fixed control that belongs to the page, not
     to the drawer. While the drawer is open it sat beside the drawer's Close
     button and read as a second control in the drawer header. The drawer is a
     modal, so for as long as it is open the launcher is taken out of sight, out
     of the tab order and out of the accessibility tree — and put straight back
     when the drawer closes. The tool itself is never disabled or removed. */
  function veePanel() { return document.getElementById('vplugin'); }
  function syncVee(open) {
    document.body.classList.toggle('ok-cart-open', !!open);
    var v = veePanel();
    if (!v) return;
    if (open) { v.setAttribute('aria-hidden', 'true'); v.setAttribute('inert', ''); }
    else { v.removeAttribute('aria-hidden'); v.removeAttribute('inert'); }
  }

  var pending = null;
  new MutationObserver(function () {
    var open = isOpen();
    syncVee(open);
    if (!open) return;
    if (pending) clearTimeout(pending);
    pending = setTimeout(focusInside, 320);
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  syncVee(isOpen());
})();
